import { createSelector } from 'reselect';
import * as T from 'frontend/@types';
import { State } from 'frontend/@types';
import { UnhandledCaseError } from 'frontend/utils';
import { getTrackFilterArtist } from 'frontend/logic/music/metadata';
import type {
  MusicPlaybackStatus,
  FolderArtworkSaveStatus,
} from 'frontend/store/reducers/music';
export type { FolderArtworkSaveStatus } from 'frontend/store/reducers/music';

export function getMusic(state: State) {
  return state.music;
}

export function getMusicTracks(state: State): T.TrackMetadata[] {
  return getMusic(state).tracks;
}

export function getMusicSelectedTrackPaths(state: State): string[] {
  return getMusic(state).selectedTrackPaths;
}

export function getMusicPanelSelections(
  state: State,
): Partial<Record<T.MusicPanelType, string[]>> {
  return getMusic(state).panelSelections;
}

export function getMusicPanelOrder(state: State): T.MusicPanelType[] {
  return getMusic(state).panelOrder;
}

export function getMusicEditTrackPath(state: State): string | null {
  return getMusic(state).editTrackPath;
}

export function getMusicEditTab(state: State): T.MusicEditTab {
  return getMusic(state).editTab;
}

export function getMusicBatchEditTrackPaths(state: State): string[] | null {
  return getMusic(state).batchEditTrackPaths;
}

export function getMusicStagingPool(state: State): T.TrackMetadata[] {
  return getMusic(state).stagingPool;
}

export function getMusicStagingView(state: State): T.MusicStagingView | null {
  return getMusic(state).stagingView;
}

export function getMusicNeedsRescan(state: State): boolean {
  return getMusic(state).needsRescan;
}

export function getMusicServedIndexVersion(state: State): number | null {
  return getMusic(state).servedIndexVersion;
}

export function getMusicServerMaxIndexVersion(state: State): number | null {
  return getMusic(state).serverMaxIndexVersion;
}

export function getFileManagerRevealLabel(state: State): string | null {
  return getMusic(state).fileManagerRevealLabel;
}

/**
 * A non-null value implies the server is running in Docker.
 */
export function getFileStoreContainerName(state: State): string | null {
  return getMusic(state).fileStoreContainerName;
}

function filterByPanel(
  filteredTracks: T.TrackMetadata[],
  panel: T.MusicPanelType,
  selections: string[],
): T.TrackMetadata[] {
  switch (panel) {
    case 'genre':
      return filteredTracks.filter(
        (t) => t.genre && selections.includes(t.genre),
      );
    case 'artist':
      return filteredTracks.filter((t) => {
        const artist = getTrackFilterArtist(t);
        return artist && selections.includes(artist);
      });
    case 'album':
      return filteredTracks.filter(
        (t) => t.album && selections.includes(t.album),
      );
    default:
      throw new UnhandledCaseError(panel, 'MusicPanelType');
  }
}

function getAvailablePanelValues(
  tracks: T.TrackMetadata[],
  panel: T.MusicPanelType,
): Set<string> {
  switch (panel) {
    case 'genre':
      return new Set(
        tracks.flatMap((track) => (track.genre ? [track.genre] : [])),
      );
    case 'artist':
      return new Set(
        tracks.flatMap((track) => {
          const artist = getTrackFilterArtist(track);
          return artist ? [artist] : [];
        }),
      );
    case 'album':
      return new Set(
        tracks.flatMap((track) => (track.album ? [track.album] : [])),
      );
    default:
      throw new UnhandledCaseError(panel, 'MusicPanelType');
  }
}

function filterByEffectivePanelSelection(
  tracks: T.TrackMetadata[],
  panel: T.MusicPanelType,
  selections: string[] | undefined,
): T.TrackMetadata[] {
  if (!selections || selections.length === 0) {
    return tracks;
  }

  const available = getAvailablePanelValues(tracks, panel);
  const effectiveSelections = selections.filter((selection) =>
    available.has(selection),
  );

  return effectiveSelections.length > 0
    ? filterByPanel(tracks, panel, effectiveSelections)
    : tracks;
}

/**
 * For each panel, the tracks available as input to that panel's item list —
 * filtered by all panels to its left. e.g. the album panel receives tracks
 * already narrowed by the artist selection.
 */
export const getMusicPanelTracks = createSelector(
  getMusicTracks,
  getMusicPanelSelections,
  getMusicPanelOrder,
  (
    allTracks,
    panelSelections,
    panelOrder,
  ): Record<T.MusicPanelType, T.TrackMetadata[]> => {
    const result = {} as Record<T.MusicPanelType, T.TrackMetadata[]>;
    let filtered = allTracks;
    for (const panel of panelOrder) {
      result[panel] = filtered;
      filtered = filterByEffectivePanelSelection(
        filtered,
        panel,
        panelSelections[panel],
      );
    }
    return result;
  },
);

export function getMusicPlaybackStatus(state: State): MusicPlaybackStatus {
  return getMusic(state).playbackStatus;
}

export function getMusicPlaybackTrackPath(state: State): string | null {
  return getMusic(state).playingTrackPath;
}

export function getMusicPlaybackLoadId(state: State): number {
  return getMusic(state).playbackLoadId;
}

export function getMusicPlaybackQueue(state: State): T.TrackMetadata[] {
  return getMusic(state).playbackQueue.tracks;
}

export function getMusicPlaybackQueuePanelSelections(
  state: State,
): Partial<Record<T.MusicPanelType, string[]>> {
  return getMusic(state).playbackQueue.panelSelections;
}

export function getMusicFolderArtworkSaveStatus(
  state: State,
): FolderArtworkSaveStatus {
  return getMusic(state).folderArtworkSaveStatus;
}

export function getMusicFolderArtworkVersion(state: State): number {
  return getMusic(state).folderArtworkVersion;
}

export function getMusicFolderArtworkEmbedStatus(
  state: State,
): FolderArtworkSaveStatus {
  return getMusic(state).folderArtworkEmbedStatus;
}

export function getMusicEmbeddedArtworkRemoveStatus(
  state: State,
): FolderArtworkSaveStatus {
  return getMusic(state).embeddedArtworkRemoveStatus;
}

export function getMusicFolderArtworkRemoveStatus(
  state: State,
): FolderArtworkSaveStatus {
  return getMusic(state).folderArtworkRemoveStatus;
}

/**
 * All panel filters applied in order — the final track list for the tracks view.
 */
export const getFilteredMusicTracks = createSelector(
  getMusicTracks,
  getMusicPanelSelections,
  getMusicPanelOrder,
  (allTracks, panelSelections, panelOrder) =>
    panelOrder.reduce((filtered, panel) => {
      return filterByEffectivePanelSelection(
        filtered,
        panel,
        panelSelections[panel],
      );
    }, allTracks),
);
